/**
 * Personalisation options
 *
 * The theme's <product-form> builds the cart payload with new FormData(form) and
 * strips empty values, so uploads and text properties reach the cart without us
 * touching the submit. This handles:
 *
 *   - revealing a checkbox's child fields when it is ticked
 *   - keeping each group's summary property in sync with what is ticked
 *   - validating required fields and upload size before the form submits
 *   - adding priced options as linked charge lines, AFTER the parent add lands
 *   - keeping those charge lines in step with their parent on the cart page
 */
(function () {
  'use strict';

  // Module scripts are de-duplicated by URL, but a second execution would wrap
  // window.fetch twice and fire every hook in duplicate.
  if (window.__personalisationOptionsLoaded) return;
  window.__personalisationOptionsLoaded = true;

  var MAX_UPLOAD_BYTES = 20 * 1024 * 1024; // Shopify hard cap
  var RELOAD_KEY = 'personalisationReconcileReloads';
  var PENDING_KEY = 'personalisationPendingCharge';
  var PENDING_TTL_MS = 20000;
  var POLL_MS = 300;
  var pending = null;
  // Taken before watchFetch wraps it, so our own cart updates are not mistaken
  // for the theme's
  var nativeFetch = window.fetch.bind(window);
  var reconciling = false;

  /**
   * Diagnostic log, off unless switched on in this browser with
   *   localStorage.pfDebug = '1'
   * Customers never see it. Each line is prefixed and timed in ms since load.
   */
  var DEBUG = false;
  try { DEBUG = localStorage.getItem('pfDebug') === '1'; } catch (e) { /* storage blocked */ }
  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[pf ' + Math.round(performance.now()) + ']');
    console.log.apply(console, args);
  }

  /* ---------------------------------------------------------------- product */

  /**
   * DPO suppression: REMOVED 2026-09-07 after two live incidents.
   *
   * This used to delete #itoris_dynamicproductoptions so that assigning the
   * metafield was the only step needed to migrate a product. It caused DPO's
   * own options.js to crash ("Cannot read properties of null"), and because DPO
   * relocates the add to cart button during init, a mid-init crash left live
   * products with no way to buy them.
   *
   * A guard against removing the button was not enough: the problem is two
   * scripts mutating the same DOM, which cannot be made safe from the outside.
   *
   * Migrating a product is therefore TWO steps, not one:
   *   1. assign custom.personalisation_set
   *   2. remove it from DPO's targeting rules
   *
   * At scale that is one action per template, not per product, which is a price
   * worth paying. Do not reintroduce this.
   */

  /* ------------------------------------------------------- searchable picker */

  /**
   * Turn a <select> whose options carry swatches into a searchable picker with
   * thumbnails, matching what DPO does for ribbons and boxes.
   *
   * Progressive enhancement: the native select stays in the DOM, keeps its
   * name attribute and remains the single source of truth. If this code never
   * runs, the customer gets a working dropdown rather than nothing.
   *
   * Left alone when no option has a swatch. Fonts and sizes read better as text
   * and the native control is better on mobile.
   */
  function enhanceSelect(select) {
    var opts = Array.prototype.slice.call(select.options);
    var hasSwatch = opts.some(function (o) {
      return o.dataset.swatch || o.dataset.swatchColour;
    });
    if (!hasSwatch || select.dataset.pfEnhanced) return;
    select.dataset.pfEnhanced = '1';

    var SEARCH_FROM = 10;   // below this a search box is more clutter than help
    var wrap = document.createElement('div');
    wrap.className = 'personalisation__combo';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'personalisation__combo-button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    if (select.id) button.setAttribute('aria-labelledby', select.id + '-label ' + select.id + '-value');

    var panel = document.createElement('div');
    panel.className = 'personalisation__combo-panel';
    panel.hidden = true;

    var search = document.createElement('input');
    search.type = 'search';
    search.className = 'personalisation__combo-search';
    search.placeholder = 'Search';
    search.setAttribute('aria-label', 'Search options');
    if (opts.length < SEARCH_FROM) search.hidden = true;

    var list = document.createElement('ul');
    list.className = 'personalisation__combo-list';
    list.setAttribute('role', 'listbox');

    var empty = document.createElement('p');
    empty.className = 'personalisation__combo-empty';
    empty.textContent = 'No matches';
    empty.hidden = true;

    function swatchEl(o) {
      var s = document.createElement('span');
      s.className = 'personalisation__combo-swatch';
      if (o && o.dataset.swatch) {
        var img = document.createElement('img');
        img.src = o.dataset.swatch;
        img.alt = '';
        img.loading = 'lazy';
        s.appendChild(img);
      } else if (o && o.dataset.swatchColour) {
        s.style.background = o.dataset.swatchColour;
      } else {
        s.className += ' @empty';
      }
      return s;
    }

    var items = opts.map(function (o, i) {
      var li = document.createElement('li');
      li.className = 'personalisation__combo-option';
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', String(o.selected));
      li.dataset.index = String(i);
      li.appendChild(swatchEl(o));

      var label = document.createElement('span');
      label.className = 'personalisation__combo-option-label';
      label.textContent = o.textContent.trim();
      li.appendChild(label);
      list.appendChild(li);
      return li;
    });

    function paintButton() {
      var o = select.options[select.selectedIndex];
      button.textContent = '';
      button.appendChild(swatchEl(o));
      var v = document.createElement('span');
      v.className = 'personalisation__combo-value';
      if (select.id) v.id = select.id + '-value';
      v.textContent = o ? o.textContent.trim() : '';
      button.appendChild(v);
      var caret = document.createElement('span');
      caret.className = 'personalisation__combo-caret';
      caret.setAttribute('aria-hidden', 'true');
      button.appendChild(caret);
    }

    var active = -1;
    function setActive(i) {
      var visible = items.filter(function (li) { return !li.hidden; });
      if (!visible.length) return;
      i = Math.max(0, Math.min(i, visible.length - 1));
      items.forEach(function (li) { li.classList.remove('@active'); });
      var li = visible[i];
      li.classList.add('@active');
      li.scrollIntoView({ block: 'nearest' });
      active = Number(li.dataset.index);
    }

    function open() {
      panel.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      search.value = '';
      filter('');
      var sel = items.findIndex(function (li) { return li.getAttribute('aria-selected') === 'true'; });
      setActive(Math.max(0, items.filter(function (li) { return !li.hidden; })
        .indexOf(items[sel === -1 ? 0 : sel])));
      (search.hidden ? list : search).focus();
    }

    function close(focusButton) {
      panel.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      if (focusButton) button.focus();
    }

    function choose(i) {
      select.selectedIndex = i;
      items.forEach(function (li, n) { li.setAttribute('aria-selected', String(n === i)); });
      paintButton();
      // Fire on the native select so every other listener behaves as before.
      select.dispatchEvent(new Event('change', { bubbles: true }));
      close(true);
    }

    function filter(q) {
      q = q.trim().toLowerCase();
      var any = false;
      items.forEach(function (li) {
        var hit = !q || li.textContent.toLowerCase().indexOf(q) !== -1;
        li.hidden = !hit;
        if (hit) any = true;
      });
      empty.hidden = any;
    }

    button.addEventListener('click', function () {
      panel.hidden ? open() : close(true);
    });

    button.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        open();
      }
    });

    search.addEventListener('input', function () { filter(search.value); setActive(0); });

    panel.addEventListener('keydown', function (e) {
      var visible = items.filter(function (li) { return !li.hidden; });
      var pos = visible.indexOf(items[active]);
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(pos + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(pos - 1); }
      else if (e.key === 'Enter') { e.preventDefault(); if (active > -1) choose(active); }
      else if (e.key === 'Escape') { e.preventDefault(); close(true); }
      else if (e.key === 'Tab') { close(false); }
    });

    list.addEventListener('click', function (e) {
      var li = e.target.closest('.personalisation__combo-option');
      if (li) choose(Number(li.dataset.index));
    });

    document.addEventListener('click', function (e) {
      if (!panel.hidden && !wrap.contains(e.target)) close(false);
    });

    panel.appendChild(search);
    panel.appendChild(list);
    panel.appendChild(empty);
    wrap.appendChild(button);
    wrap.appendChild(panel);
    select.parentNode.insertBefore(wrap, select);

    // Kept in the DOM and still named, so it submits. Hidden from sight and from
    // the accessibility tree, since the button and listbox replace it there.
    select.classList.add('personalisation__select--enhanced');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    select.addEventListener('change', paintButton);
    paintButton();
  }

  function initProduct(root) {
    var form = document.getElementById(root.dataset.formId);
    if (!form) return;

    var groupInput = root.querySelector('[data-personalisation-group]');
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-pf-group]'));
    var checkboxes = Array.prototype.slice.call(root.querySelectorAll('[data-pf-checkbox]'));

    // Declared before sync(), which calls chosenCharges() on its first run.
    // var hoisting would leave these undefined if they sat further down.
    var radios = Array.prototype.slice.call(root.querySelectorAll('[data-pf-choice]'));
    var selects = Array.prototype.slice.call(root.querySelectorAll('[data-pf-select]'));

    function panelFor(el) {
      return el.dataset.panel ? root.querySelector(el.dataset.panel) : null;
    }

    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-pf-panel]'));

    /**
     * Fields this panel owns directly, ignoring anything sitting inside a
     * panel nested within it. A nested panel is responsible for its own
     * fields; if the outer panel enabled them too, opening "Add Engraving"
     * would enable the upload hidden behind "I'd like to upload a file"
     * without that option being chosen.
     */
    function ownFields(panel) {
      return Array.prototype.filter.call(
        panel.querySelectorAll('input, select, textarea'),
        function (f) { return f.closest('[data-pf-panel]') === panel; }
      );
    }

    function setPanel(panel, open) {
      panel.hidden = !open;
      // Disabled fields are omitted from FormData, so a closed panel can never
      // contribute a property, and validation skips it because it is disabled.
      ownFields(panel).forEach(function (f) {
        f.disabled = !open;
      });
    }

    /**
     * One pass over every reveal panel rather than per-control toggling.
     *
     * Radios and selects are single-choice, so switching between two options
     * that each reveal something has to close the old panel as well as open the
     * new one. Closing everything first makes that fall out for free.
     */
    function syncPanels() {
      var opened = false;

      /**
       * A control inside a closed panel is disabled, and a disabled control
       * must not open anything: its own tick is only meaningful once the
       * question containing it is actually on screen. This is what stops a
       * stale tick deep in a closed branch from re-enabling its fields.
       */
      function openFor(el) {
        if (!el || !el.dataset.panel || el.disabled) return;
        var panel = root.querySelector(el.dataset.panel);
        if (panel && panel.hidden) {
          setPanel(panel, true);
          opened = true;
        }
      }

      panels.forEach(function (p) { setPanel(p, false); });

      // Outer panels have to open before the controls they contain stop being
      // disabled, so one pass is not enough once panels nest. Each pass opens
      // the next level down; the loop settles when a pass opens nothing. The
      // guard is a backstop against a set that somehow references itself.
      var guard = 0;
      do {
        opened = false;
        checkboxes.forEach(function (c) { if (c.checked) openFor(c); });
        radios.forEach(function (r) { if (r.checked) openFor(r); });
        // The element handed to openFor is an <option>, whose own disabled
        // flag says nothing about whether the select is reachable, so the
        // select is checked here instead.
        selects.forEach(function (s) {
          if (!s.disabled) openFor(s.selectedOptions && s.selectedOptions[0]);
        });
      } while (opened && ++guard < 10);
    }

    function sync() {
      var anyCharge = false;

      syncPanels();

      checkboxes.forEach(function (checkbox) {
        if (checkbox.checked && !checkbox.disabled && checkbox.dataset.addonVariant) {
          anyCharge = true;
        }
      });

      if (!anyCharge && chosenCharges().length) anyCharge = true;

      groups.forEach(function (group) {
        // Both lookups are scoped to this group's own level. A group can now
        // contain another group, and without this the parent's property would
        // swallow the child's choices: "Customise Your Item" would read
        // "Add Engraving, Arial" instead of leaving the font to its own line.
        var summary = Array.prototype.filter.call(
          group.querySelectorAll('[data-pf-summary]'),
          function (s) { return s.closest('[data-pf-group]') === group; }
        )[0];
        if (!summary) return;

        var chosen = Array.prototype.filter
          .call(group.querySelectorAll('[data-pf-checkbox]'), function (c) {
            // Disabled means the box sits in a branch that is closed, so its
            // tick is not something the customer is currently asking for.
            return c.checked && !c.disabled && c.closest('[data-pf-group]') === group;
          })
          .map(function (c) { return c.dataset.summary; });

        summary.value = chosen.join(', ');
        summary.disabled = chosen.length === 0;
      });

      // _group only earns its place when there is a second line to link to.
      if (groupInput) groupInput.disabled = !anyCharge;
    }

    checkboxes.forEach(function (c) { c.addEventListener('change', sync); });

    // Run once now, and again on pageshow. Browsers restore checkbox state on a
    // soft reload and on back-navigation, so a checkbox can come back ticked
    // while its panel is still hidden and its fields still disabled.
    sync();
    window.addEventListener('pageshow', sync);

    root.querySelectorAll('[data-personalisation-tip]').forEach(function (button) {
      button.addEventListener('click', function () {
        var tip = document.getElementById(button.getAttribute('aria-controls'));
        if (!tip) return;
        tip.hidden = !tip.hidden;
        button.setAttribute('aria-expanded', String(!tip.hidden));
      });
    });

    /**
     * Priced options chosen from a radio group or a dropdown. Same shape as the
     * checkbox charges, so they join the same cart sequencing.
     */
    function chosenCharges() {
      var out = [];

      // Disabled controls sit inside a closed branch. The customer cannot see
      // them, so they must never put a charge line in the basket.
      radios.forEach(function (r) {
        if (r.checked && !r.disabled && r.dataset.addonVariant) {
          out.push({ id: Number(r.dataset.addonVariant), label: r.value });
        }
      });

      selects.forEach(function (s) {
        if (s.disabled) return;
        var opt = s.selectedOptions && s.selectedOptions[0];
        if (opt && opt.dataset.addonVariant) {
          out.push({ id: Number(opt.dataset.addonVariant), label: s.value });
        }
      });

      return out;
    }

    /** A required choice is unsatisfied when nothing is picked. */
    function missingChoice() {
      var found = null;

      // A question inside a closed reveal is not being asked, so it cannot be
      // unanswered. Without this the form would refuse to submit and point at
      // a field the customer cannot see.
      root.querySelectorAll('fieldset[data-pf-required-choice]').forEach(function (fs) {
        if (found || fs.closest('[data-pf-panel][hidden]')) return;
        var picked = fs.querySelector('[data-pf-choice]:checked');
        if (!picked) found = fs.querySelector('[data-pf-choice]');
      });

      selects.forEach(function (s) {
        if (found || s.disabled || !s.hasAttribute('data-pf-required-choice')) return;
        if (s.value === '') found = s;
      });

      return found;
    }

    // Keep the charge state fresh: a radio or dropdown change can add or remove
    // a priced option, which decides whether _group needs to travel.
    radios.concat(selects).forEach(function (el) {
      el.addEventListener('change', sync);
    });

    selects.forEach(enhanceSelect);


    /* ----------------------------------------------------------- validation */

    function errorSlotFor(el) {
      var block = el.closest('.personalisation__block');
      return block ? block.querySelector('[data-pf-error]') : null;
    }

    function setError(el, message) {
      var slot = errorSlotFor(el);
      if (slot) {
        slot.hidden = !message;
        slot.textContent = message || '';
      }
      el.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    function clearErrors() {
      root.querySelectorAll('[data-pf-error]').forEach(function (slot) {
        slot.hidden = true;
        slot.textContent = '';
      });
      root.querySelectorAll('[aria-invalid="true"]').forEach(function (el) {
        el.setAttribute('aria-invalid', 'false');
      });
    }

    function isEmpty(el) {
      return el.type === 'file'
        ? !(el.files && el.files.length)
        : el.value.trim() === '';
    }

    root.querySelectorAll('[data-pf-required]').forEach(function (el) {
      var ev = el.type === 'file' ? 'change' : 'input';
      el.addEventListener(ev, function () {
        if (!isEmpty(el)) setError(el, '');
      });
    });

    root.querySelectorAll('[data-pf-file]').forEach(function (input) {
      input.addEventListener('change', function () {
        var file = input.files && input.files[0];
        if (!file || file.size <= MAX_UPLOAD_BYTES) return setError(input, '');
        var mb = (file.size / 1024 / 1024).toFixed(1);
        input.value = '';
        setError(input,
          'That file is ' + mb + 'MB. The maximum is 20MB. Please send larger ' +
          'artwork by email once you have placed your order.');
      });
    });

    /**
     * Returns the first element to complain about, or null when good to submit.
     * Only visible fields count: a field inside a closed panel is disabled and
     * cannot reach the order, so it cannot be required.
     */
    function validate() {
      var oversized = null;
      root.querySelectorAll('[data-pf-file]').forEach(function (input) {
        if (input.disabled) return;
        var file = input.files && input.files[0];
        if (file && file.size > MAX_UPLOAD_BYTES) oversized = oversized || input;
      });
      if (oversized) {
        return { el: oversized, message: 'Please choose a file under 20MB before adding to your basket.' };
      }

      var missing = null;
      root.querySelectorAll('[data-pf-required]').forEach(function (el) {
        if (el.disabled || missing) return;
        if (isEmpty(el)) missing = el;
      });
      if (missing) {
        return {
          el: missing,
          message: missing.type === 'file'
            ? 'Please choose a file before adding to your basket.'
            : 'Please complete this before adding to your basket.'
        };
      }

      var noChoice = missingChoice();
      if (noChoice) {
        return { el: noChoice, message: 'Please choose an option before adding to your basket.' };
      }

      var emptyGroup = null;
      root.querySelectorAll('[data-pf-required-group]').forEach(function (summary) {
        // A required group inside a closed reveal reads as "nothing ticked"
        // for the same reason it is invisible, so it must not be enforced.
        if (emptyGroup || summary.closest('[data-pf-panel][hidden]')) return;
        if (summary.disabled) emptyGroup = summary;   // disabled means nothing ticked
      });
      if (emptyGroup) {
        var firstBox = emptyGroup.closest('[data-pf-group]').querySelector('[data-pf-checkbox]');
        return { el: firstBox, message: 'Please choose an option before adding to your basket.' };
      }

      return null;
    }

    function reportProblem(problem) {
      setError(problem.el, problem.message);
      problem.el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      if (problem.el.focus) problem.el.focus({ preventScroll: true });
    }

    /**
     * Block at click, not at submit.
     *
     * The theme's button enters its loading state before the submit event is
     * dispatched, and its own submit handler is what clears it again. Cancelling
     * the submit leaves the button spinning forever. Cancelling the click means
     * the form never submits, so the loading state is never entered.
     */
    document.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('button, [type="submit"]');
      if (!button) return;

      var owner = button.form || document.getElementById(button.getAttribute('form'));
      if (owner !== form) return;
      if (button.type && button.type !== 'submit') return;

      var problem = validate();
      if (!problem) return;

      event.preventDefault();
      event.stopPropagation();
      reportProblem(problem);
    }, true);

    /** Backstop for Enter-key and programmatic submits. */
    document.addEventListener('submit', function (event) {
      if (event.target !== form) return;

      var problem = validate();
      if (problem) {
        event.preventDefault();
        event.stopPropagation();
        reportProblem(problem);
        return;
      }
      clearErrors();

      var addons = checkboxes
        .filter(function (c) {
          return c.checked && !c.disabled && c.dataset.addonVariant;
        })
        .map(function (c) {
          return { id: Number(c.dataset.addonVariant), label: c.dataset.summary };
        })
        .concat(chosenCharges());

      if (!addons.length) return;

      var groupId = 'pf-' + Math.random().toString(36).slice(2, 10);
      if (groupInput) groupInput.value = groupId;

      schedule({
        groupId: groupId,
        quantity: chosenQuantity(form),
        addons: addons,
        at: Date.now()
      });
    }, true);
  }

  /**
   * The quantity the customer chose, or 0 when no quantity field is found.
   *
   * This theme's selector (product-qty-selector.liquid) sits outside the form
   * and joins it through form="<id>", so both places are searched. Every match
   * counts, not just the first: a second copy of the field, such as a sticky
   * add-to-cart bar, would otherwise shadow the one the customer used.
   */
  function chosenQuantity(form) {
    var fields = Array.prototype.slice.call(form.querySelectorAll('[name="quantity"]'))
      .concat(Array.prototype.slice.call(
        document.querySelectorAll('[name="quantity"][form="' + form.id + '"]')));
    return fields.reduce(function (most, f) {
      return Math.max(most, Number(f.value) || 0);
    }, 0);
  }

  /* ------------------------------------------------------------------- cart */

  /**
   * Charge lines are added AFTER the theme's own add has landed, never alongside
   * it. Firing both at once and navigating on our own response aborts the
   * theme's in-flight request, whose multipart body carries the upload and is
   * always the slower of the two. That shipped between 2026-08-20 and
   * 2026-09-03 and left customers with a charge line and no product.
   *
   * The job is parked in sessionStorage so it survives the theme navigating to
   * /cart before we have finished.
   */
  function schedule(job) {
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(job));
    } catch (e) { /* private mode: in-memory poll still works */ }
    pending = job;
    awaitParent(job);
  }

  function readPending() {
    if (pending) return pending;
    try {
      var raw = sessionStorage.getItem(PENDING_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function clearPending() {
    pending = null;
    try { sessionStorage.removeItem(PENDING_KEY); } catch (e) { /* nothing to clear */ }
  }

  /**
   * Poll until the parent line is actually in the cart. Giving up without adding
   * is deliberate: no charge line beats a charge line with nothing attached.
   */
  function awaitParent(job) {
    if (Date.now() - job.at > PENDING_TTL_MS) {
      console.warn('[personalisation] parent line never appeared, charge line not added');
      return clearPending();
    }

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var parentQty = 0, already = false;
        cart.items.forEach(function (item) {
          var p = item.properties || {};
          if (p._group !== job.groupId) return;
          if (p._addon_for) already = true;
          else parentQty = item.quantity;
        });

        if (already) return clearPending();       // resumed after a reload
        // The cart is authoritative. The quantity read from the DOM at click
        // time is only a fallback: see addCharges.
        if (parentQty) return addCharges(job, parentQty);
        setTimeout(function () { awaitParent(job); }, POLL_MS);
      })
      .catch(function () {
        setTimeout(function () { awaitParent(job); }, POLL_MS);
      });
  }

  /**
   * Charged at the higher of the cart's quantity and the one the customer chose.
   *
   * Neither alone is enough. The field alone undercharged in 2026-09 (§6c) when
   * it was not found and the lookup fell back to 1. The cart alone undercharged
   * in 2026-10 (§6d): the parent line first appears at quantity 1 and the theme
   * raises it a moment later, after we have read it. The basket then opened at
   * the wrong price and reconcile had to reload it.
   *
   * Erring high is safe: reconcile on /cart and at checkout brings any charge
   * line back to its parent's quantity, in either direction.
   */
  function addCharges(job, parentQty) {
    clearPending();

    var quantity = Math.max(parentQty || 0, job.quantity || 0) || 1;

    var items = job.addons.map(function (addon) {
      return {
        id: addon.id,
        quantity: quantity,
        properties: { _group: job.groupId, _addon_for: addon.label }
      };
    });

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (json) {
            throw new Error(json.description || json.message || 'charge line failed');
          });
        }
        if (onCartPage()) window.location.reload();
        else window.location = '/cart';
      })
      .catch(function (error) {
        console.error('[personalisation] could not add the charge line:', error);
      });
  }

  /**
   * Match every charge line to its parent via the shared _group property.
   * Charge lines whose parent is gone are removed; the rest follow its quantity.
   */
  /**
   * Bring charge lines into line with their parents. Resolves false when
   * nothing needed changing, otherwise { sections }: the named cart sections
   * rendered after the change (empty when none were asked for).
   *
   * Split out from reconcileCart so the checkout guard can reuse it without
   * triggering a page reload: at that point the customer is on their way out,
   * and reloading the cart under them would be worse than pointless.
   */
  function reconcileOnce(sectionIds) {
    return fetch('/cart.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var parents = {};
        cart.items.forEach(function (item) {
          var p = item.properties || {};
          if (p._group && !p._addon_for) parents[p._group] = item.quantity;
        });

        var updates = {};
        cart.items.forEach(function (item) {
          var p = item.properties || {};
          if (!p._addon_for) return;
          var wanted = parents[p._group] || 0;
          if (item.quantity !== wanted) updates[item.key] = wanted;
        });

        log('reconcile read', cart.items.map(function (i) {
          return (i.properties && i.properties._addon_for ? 'charge ' : 'item ') + i.quantity;
        }).join(', '), '| updates', JSON.stringify(updates));
        if (!Object.keys(updates).length) return false;

        var body = { updates: updates };
        // Bundled section rendering: the same request returns the cart sections
        // redrawn at the new quantities, so the page need not reload.
        if (sectionIds && sectionIds.length) {
          body.sections = sectionIds;
          body.sections_url = window.location.pathname;
        }

        return nativeFetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            log('update done; sections received', Object.keys(cart.sections || {}).join(', ') || 'none');
            return { sections: cart.sections || {} };
          });
      });
  }

  /**
   * Every cart section on the page, by the theme's live regions. There is more
   * than one: the header carries its own, and it comes first. Taking only the
   * first redrew the header and left the cart itself stale (§6e). Shopify
   * renders at most five sections per request.
   */
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-live-region[section-id]').forEach(function (r) {
      var id = r.getAttribute('section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids.slice(0, 5);
  }

  /**
   * Swap each live region's contents for the freshly rendered ones, matched by
   * section id. Only the inside is replaced: the region elements themselves,
   * and the theme's hold on them, stay put, and the custom elements inside
   * upgrade again as they arrive. Returns whether the main cart was redrawn
   * (#mainCartLiveRegion, from s_cart.liquid), or failing that whether every
   * section was, so the caller can fall back to a reload. A header or drawer
   * section that will not render on /cart must not force one.
   */
  function swapCartRegions(sections) {
    var ids = Object.keys(sections || {});
    if (!ids.length) return false;
    var done = ids.filter(function (id) {
      var doc = new DOMParser().parseFromString(sections[id] || '', 'text/html');
      var fresh = doc.querySelector('cart-live-region[section-id="' + id + '"]') ||
        doc.querySelector('cart-live-region');
      var current = document.querySelector('cart-live-region[section-id="' + id + '"]');
      log('swap', id, '| fresh region', !!fresh, '| page region', !!current);
      if (!fresh || !current) return false;
      current.innerHTML = fresh.innerHTML;
      return true;
    });
    // Our own swaps are not something to react to
    if (regionObserver) regionObserver.takeRecords();
    var main = document.getElementById('mainCartLiveRegion');
    var mainId = main && main.getAttribute('section-id');
    return mainId ? done.indexOf(mainId) !== -1 : done.length === ids.length;
  }

  /**
   * Keep our redraw on screen if the theme paints over it with an older one.
   *
   * A theme redraw rendered before our charge update landed would carry the
   * old charges. Ours was rendered after, so it is the newer of the two.
   * Re-applied only while no new quantity change has been made since, and only
   * for a short window: a new change starts a fresh reconcile of its own.
   */
  var KEEP_RENDER_MS = 5000;
  var mutationSeq = 0;      // theme cart changes seen so far
  var lastRender = null;    // { sections, seq, at } of our latest swap
  var regionObserver = null;

  function keepRender(sections, seq) {
    lastRender = { sections: sections, seq: seq, at: Date.now() };
    if (regionObserver) return;

    regionObserver = new MutationObserver(function (records) {
      if (!lastRender) return;
      // A redraw is anything inside a region being replaced, or a region
      // itself. Nodes only added, such as a spinner, are left alone.
      var redrawn = records.some(function (r) {
        var removed = Array.prototype.some.call(r.removedNodes, function (n) {
          return n.nodeType === 1;
        });
        if (!removed) return false;
        var inside = r.target.closest && r.target.closest('cart-live-region');
        return !!inside || Array.prototype.some.call(r.removedNodes, function (n) {
          return n.nodeType === 1 && (n.matches('cart-live-region') || n.querySelector('cart-live-region'));
        });
      });
      if (!redrawn) return;
      if (lastRender.seq !== mutationSeq || Date.now() - lastRender.at > KEEP_RENDER_MS) {
        log('observer: not re-applying (newer change or window passed)');
        lastRender = null;
        return;
      }
      log('observer: theme redrew over us, re-applying our render');
      swapCartRegions(lastRender.sections);
    });
    // The whole body: the regions sit in different sections of the page
    regionObserver.observe(document.body, { childList: true, subtree: true });
  }

  var rerun = false;        // a change arrived while reconcile was busy

  function reconcileCart() {
    if (reconciling) {
      rerun = true;
      return Promise.resolve();
    }
    reconciling = true;
    rerun = false;
    var seq = mutationSeq;
    log('reconcile start, seq', seq);

    return reconcileOnce(cartSectionIds())
      .then(function (changed) {
        var swapped = !!changed && swapCartRegions(changed.sections);
        if (swapped) keepRender(changed.sections, seq);
        if (!changed || swapped) {
          sessionStorage.removeItem(RELOAD_KEY);
          return null;
        }

        // Fallback when the section could not be swapped in.
        return Promise.resolve().then(function () {
          // Guarded: an unguarded reload here would loop forever if an update
          // ever failed to change the cart.
          var reloads = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
          if (reloads >= 2) {
            console.warn('[personalisation] cart reconcile did not settle; not reloading again');
            return;
          }
          sessionStorage.setItem(RELOAD_KEY, String(reloads + 1));
          window.location.reload();
        });
      })
      .finally(function () {
        reconciling = false;
        // A press made mid-reconcile was skipped; catch up now
        if (rerun && onCartPage()) setTimeout(reconcileCart, 0);
      });
  }

  /** Cart-page only: the theme mutates quantities through calls we cannot name. */
  function watchFetch() {
    var original = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isMutation = /\/cart\/(change|update|clear)(\.js)?/.test(url);
      if (/cart|section/.test(url)) log('fetch', url.slice(0, 120), isMutation ? '(mutation)' : '');
      // Our own update runs while reconciling; only the theme's changes count
      if (isMutation && !reconciling) mutationSeq++;
      return original.apply(this, arguments).then(function (response) {
        if (isMutation && onCartPage()) {
          if (reconciling) rerun = true;
          else setTimeout(reconcileCart, 0);
        }
        return response;
      });
    };
  }

  /**
   * Verify the cart at the moment the customer commits, not only when we happen
   * to notice a change.
   *
   * The race this closes: the quantity stepper updates the product line first,
   * reconcile follows a moment later. Between the two, the cart is wrong. A
   * customer who steps the quantity and immediately hits checkout leaves with
   * charge lines at the old quantity, and the shop is undercharged. That is the
   * same shape as the 2026-08 incident, where navigation beat a slower request.
   *
   * Checking here rather than only on mutation also means correctness no longer
   * depends on us recognising every way the theme might change a quantity. Even
   * if the fetch hook misses it entirely, this catches it on the way out.
   *
   * Fails open. If the check is slow or errors, the customer proceeds. A shop
   * that undercharges occasionally is a smaller problem than a checkout button
   * that does not work.
   */
  var CHECKOUT_WAIT_MS = 4000;

  function guardCheckout() {
    document.addEventListener('click', function (e) {
      if (!onCartPage()) return;

      var el = e.target && e.target.closest
        ? e.target.closest('[name="checkout"], a[href*="/checkout"]')
        : null;
      if (!el) return;

      // Our own re-dispatch, or a second click while we are still working.
      if (el.dataset.pfVerified === '1') return;
      if (el.dataset.pfVerifying === '1') {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();

      el.dataset.pfVerifying = '1';
      el.setAttribute('aria-busy', 'true');

      // Hold off the mutation hook's reconcile while we work. Without this it
      // can reload the page underneath a customer who has already committed,
      // losing the click and leaving them staring at the cart again.
      reconciling = true;

      var timeout = new Promise(function (resolve) {
        setTimeout(resolve, CHECKOUT_WAIT_MS);
      });

      Promise.race([reconcileOnce().catch(function () { return false; }), timeout])
        .then(function () {
          delete el.dataset.pfVerifying;
          el.removeAttribute('aria-busy');
          reconciling = false;

          // Cleared immediately after. click() dispatches synchronously, so the
          // theme's handler has already run by the time this returns. Leaving
          // the flag set would let a later click through unchecked, which the
          // back button and bfcache make a real possibility rather than a
          // theoretical one.
          el.dataset.pfVerified = '1';
          el.click();
          delete el.dataset.pfVerified;
        });
    }, true);
  }

  function onCartPage() {
    return window.location.pathname.indexOf('/cart') === 0;
  }

  /* ------------------------------------------------------------------- init */

  function init() {
    log('script loaded on', window.location.pathname);
    watchFetch();
    guardCheckout();
    document.querySelectorAll('[data-personalisation]').forEach(initProduct);

    var job = readPending();
    if (job) awaitParent(job);
    else if (onCartPage()) reconcileCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.personalisationReconcileCart = reconcileCart;
})();